import type { GatewayBrowserClient } from "../gateway.ts";

/** Step status union mirrors PLAN_STEP_STATUSES (src/agents/tools/plan-tool.helpers.ts). */
export type AgentPlanStep = {
  id?: string;
  text: string;
  status: "pending" | "in_progress" | "done" | "skipped";
};

export type AgentPlanStatus = "active" | "completed" | "abandoned";

/**
 * One row from `igloobot.plans.list` — the agent_plans DB projection
 * (src/igloobot/db/schema.ts). Timestamps arrive as ISO strings over the wire.
 */
export type AgentPlanEntry = {
  id: string;
  planId: string;
  sessionKey: string;
  sessionId: string;
  projectId: string | null;
  agentId: string | null;
  title: string | null;
  steps: AgentPlanStep[];
  status: AgentPlanStatus;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
};

export type AgentPlansStatusFilter = "all" | AgentPlanStatus;

export const AGENT_PLANS_STATUS_FILTERS: Array<{
  value: AgentPlansStatusFilter;
  label: string;
}> = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "completed", label: "Completed" },
  { value: "abandoned", label: "Abandoned" },
];

export type AgentPlansState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  agentPlansLoading: boolean;
  agentPlans: AgentPlanEntry[] | null;
  agentPlansError: string | null;
  agentPlansStatusFilter: AgentPlansStatusFilter;
};

export async function loadAgentPlans(state: AgentPlansState) {
  if (!state.client || !state.connected) {
    return;
  }
  if (state.agentPlansLoading) {
    return;
  }
  state.agentPlansLoading = true;
  state.agentPlansError = null;
  try {
    const params: { limit: number; status?: AgentPlanStatus } = { limit: 100 };
    if (state.agentPlansStatusFilter !== "all") {
      params.status = state.agentPlansStatusFilter;
    }
    const res = await state.client.request<{ ok: boolean; plans?: AgentPlanEntry[] }>(
      "igloobot.plans.list",
      params,
    );
    state.agentPlans = res.plans ?? [];
  } catch (err) {
    state.agentPlansError = String(err);
  } finally {
    state.agentPlansLoading = false;
  }
}
